import { sanitizeAiRules } from "common/utils/sanitizeAiRules";
import { resolveThemeAiImageStyle } from "common/utils/themeAiSettings";
import { getState, trackState } from "js/analytics/util";
import { getThemeAiRules, readThemeAttribute } from "js/core/utilities/themeAiDefaults";
import { app } from "js/namespaces";

/** A theme as the call sites hold it: a loaded model, plain attributes, or none. */
type TrackableTheme = { id?: string, root?: string, get?: (key: string) => any, [key: string]: any } | null | undefined;

/** The storage root a shared (team) theme model is loaded from. */
const SHARED_THEME_ROOT = "sharedThemes";

// Plain attributes carry no marker for this, so it reports null rather than
// guessing "personal".
function resolveIsShared(theme: TrackableTheme, isShared: boolean | null) {
    if (isShared !== null) {
        return isShared;
    }

    return typeof theme?.get === "function" ? theme.root === SHARED_THEME_ROOT : null;
}

export interface BrandKitTrackingProps {
    brand_kit_theme_id: string | null;
    /** Null when the caller can't tell a shared kit from a personal one. */
    brand_kit_is_shared: boolean | null;
    brand_kit_voice_applied: boolean;
    brand_kit_instructions_applied: boolean;
    brand_kit_language_applied: boolean;
    brand_kit_image_defaults_applied: boolean;
    brand_kit_ai_configured: boolean;
    brand_kit_ai_fields_count: number;
}

/**
 * The kit's AI settings, reduced to what analytics may record: whether each is
 * set, never the wording, since guidance and image prompts are the user's own
 * brand copy. A setting counts as configured only when it says something the kit
 * wouldn't have said anyway - "auto" is the language every kit starts on.
 */
export function getBrandKitTrackingProps(theme: TrackableTheme, { isShared = null }: { isShared?: boolean | null } = {}): BrandKitTrackingProps {
    const { voiceAndTone, presentationInstructions, language, imageSource } = getThemeAiRules(theme);
    const voice = sanitizeAiRules(voiceAndTone);
    const instructions = sanitizeAiRules(presentationInstructions);
    // Every theme carries a preset whether or not anyone chose it, so only a
    // custom prompt is evidence the kit's author picked a style.
    const imageStylePrompt = readThemeAttribute(theme, "imageStyle") === "custom"
        ? resolveThemeAiImageStyle({
            imageStyle: readThemeAttribute(theme, "imageStyle"),
            imageStylePrompt: readThemeAttribute(theme, "imageStylePrompt")
        })
        : undefined;

    const fields = {
        brand_kit_voice_applied: !!voice,
        brand_kit_instructions_applied: !!instructions,
        brand_kit_language_applied: !!language && language !== "auto",
        brand_kit_image_defaults_applied: !!imageSource || !!imageStylePrompt
    };

    const configuredCount = Object.values(fields).filter(Boolean).length;

    return {
        brand_kit_theme_id: theme?.id ?? readThemeAttribute(theme, "id") ?? null,
        brand_kit_is_shared: resolveIsShared(theme, isShared),
        ...fields,
        brand_kit_ai_configured: configuredCount > 0,
        brand_kit_ai_fields_count: configuredCount
    };
}

/**
 * Records for the rest of the session that the user has worked with a configured
 * brand kit, which is what lets the billing and churn events be read against kit
 * usage without knowing anything about kits. The state only escalates, and its
 * keys are distinct from the `brand_kit_*` props above - a key an event also sets
 * would be written back into the state and undo that escalation.
 */
export function trackBrandKitState(theme: TrackableTheme, { isShared = null }: { isShared?: boolean | null } = {}) {
    const props = getBrandKitTrackingProps(theme, { isShared });

    trackState({
        has_used_brand_kit_ai: !!getState("has_used_brand_kit_ai") || props.brand_kit_ai_configured,
        max_brand_kit_ai_fields: Math.max(getState("max_brand_kit_ai_fields") ?? 0, props.brand_kit_ai_fields_count)
    });
}

/** The kit the editor is working in, for surfaces that hold no theme of their own. */
export function getCurrentBrandKitTrackingProps() {
    return getBrandKitTrackingProps(app.currentTheme);
}
